import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// @mui
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from "../../components/Iconify";
import Image from '../../components/Image';
import DIOText from '../../components/DIOText';
import {
  FliersProfileLogoUploader,
  FliersProfilePositionDialog,
  FliersProfileReasonDialog,
  FliersProfileExampleDialog,
} from '../../sections/fliers/profile';
// utils
import axios from '../../utils/axios';
import {default as axiosNormal} from 'axios';

// ----------------------------------------------------------------------

export default function ProfileForm({ sx, profile, reloadProfile }) {
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const companyId = user?.employee?.company?.[0].id;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fileBlob, setFileBlob] = useState(null);
  const [quillSimple, setQuillSimple] = useState('');
  const [profileName, setProfileName] = useState('');
  const [appealTitle, setAppealTitle] = useState('');
  const [companyKeywords, setCompanyKeywords] = useState([]);
  const [selection, setSelection] = useState([]);
  const [needCasualChat, setNeedCasualChat] = useState(false);
  const [needPartTime, setNeedPartTime] = useState(false);
  const [needMiniProject, setNeedMiniProject] = useState(false);

  // dialogs
  const [openPositionDialog, setOpenPositionDialog] = useState(false);
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [openFliersProfileExampleDialog, setOpenFliersProfileExampleDialog] = useState(false);
  

  useEffect(() => {
    if (profile) {
      setFileBlob(null);
      setProfileName(profile?.name);
      setAppealTitle(profile?.appeal_title || '');
      profile?.keywords && setCompanyKeywords(profile?.keywords.split(' ').map(v => `${v}`));
      profile?.coffee_chat_positions?.length > 0 && setSelection(profile?.coffee_chat_positions);
      setNeedCasualChat(profile?.need_casual_chat);
      setNeedPartTime(profile?.need_part_time);
      setNeedMiniProject(profile?.need_mini_project);
      setQuillSimple(profile?.description);
    }
  }, [profile]);

  const fileUploadBeforeSubmit = async (f) => {
    if (!f) return null;
    const urlResponse = await axios.post(
      `/api/company/${companyId}/fliers/profile/logo-url/`,
      {filename: companyId}
    );
    const {filename, url} = urlResponse.data;

    // upload file to s3 via presigned url
    await axiosNormal.put(
      url,
      f,
      {
        headers: {
          'Content-Type': 'image/png',
          'X-Amz-ACL': 'public-read',
        }
      }
    );
    return filename;
  };

  const submitProfile = async () => {
    try {
      setIsSubmitting(true);
      const hasFile = !!fileBlob;
      const postData = {
        id: profile?.id,
        logo: hasFile ? await fileUploadBeforeSubmit(fileBlob) : null,
        name: profileName,
        appeal_title: appealTitle,
        keywords: companyKeywords.length > 0 ? companyKeywords.join(' ') : null,
        coffee_chat_positions: selection,
        need_casual_chat: needCasualChat,
        need_part_time: needPartTime,
        need_mini_project: needMiniProject,
        description: quillSimple,
      };
      await axios.post(`/api/company/${companyId}/fliers/profile/`, postData);
      setIsSubmitting(false);
      enqueueSnackbar('회사 정보가 제출되었습니다.');
      reloadProfile();
    } catch (err) {
      console.error(err);
      setIsSubmitting(false);
      enqueueSnackbar('회사 정보 제출중 오류가 발생하였습니다. 관리자에 문의해주세요.', { variant: 'error' });
    }
  };

  const saveProfile = async () => {
    try {
      setIsSubmitting(true);
      const hasFile = !!fileBlob;
      const patchData = {
        id: profile?.id,
        logo: hasFile ? await fileUploadBeforeSubmit(fileBlob) : null,
        name: profileName,
        appeal_title: appealTitle,
        keywords: companyKeywords.length > 0 ? companyKeywords.join(' ') : null,
        coffee_chat_positions: selection,
        need_casual_chat: needCasualChat,
        need_part_time: needPartTime,
        need_mini_project: needMiniProject,
        description: quillSimple,
      };
      await axios.patch(`/api/company/${companyId}/fliers/profile/`, patchData);
      setIsSubmitting(false);
      enqueueSnackbar('회사 정보가 저장되었습니다.');
      reloadProfile();
    } catch (err) {
      console.error(err);
      setIsSubmitting(false);
      enqueueSnackbar('회사 정보 저장중 오류가 발생하였습니다. 관리자에 문의해주세요.', { variant: 'error' });
    }
  };

  const updatePosition = (selectedPosition) => {
    setSelection(selectedPosition);
  };

  const updateLogo = async (file) => {
    setFileBlob(file);
    // default blob type : 'imgae/png'
  };

  return (
    <>
    <Stack sx={{ width: 1, ...sx }} spacing={3}>
    {profile?.status === 'draft' && (
      <Stack>
        <Typography variant="h5" gutterBottom>
          회사 소개를 작성해주세요.
        </Typography>
        <Typography variant="body2">
          크루가 기업의 커피챗 제안을 받으면 이 내용을 확인하게 됩니다.
        </Typography>
        <Typography variant="body2">
          영입하고 싶은 인재가 회사에 관심을 가질 수 있게 어필해 주세요.
        </Typography>
      </Stack>
      )}
      {profile?.status === 'register' && (
      <Stack spacing={1}>
        <Typography variant="h5">
          <DIOText /> 팀에서 회사 소개를 확인중이에요.
        </Typography>
      </Stack>
      )}
      <Card>
        <CardContent>
          <Typography variant="h6">
            회사 카드
          </Typography>

          {/* card form */}
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 3 }}>
              <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems="center">
                <FliersProfileLogoUploader originImageURL={profile?.logo_file_path} updateLogo={updateLogo} />
                <Typography variant="caption" sx={{ color: "text.secondary", maxWidth: 120, textAlign: smUp ? "center" : "left" }}>
                  이미지 영역을 선택하면<br/>로고 이미지를<br/>변경할 수 있습니다.
                </Typography>
              </Stack>
              <Stack sx={{ width: { xs: 1, sm: 'calc(100%-144px)' } }}>
                <Typography variant="subtitle2" gutterBottom>
                  회사 또는 서비스 이름
                </Typography>
                <Typography variant="caption" gutterBottom>
                  서비스 이름이 회사 이름보다 더 유명하다면 서비스 이름으로 수정해보세요. 예) 비바리퍼블리카 → 토스
                </Typography>
                <TextField
                  helperText="가입하신 회사 이름으로 미리 입력해두었어요."
                  FormHelperTextProps={{ sx: { ml: 0 } }}
                  sx={{ mb: 2 }}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                />

                <Typography variant="subtitle2" gutterBottom>
                  커피챗 어필 타이틀
                </Typography>
                <TextField
                  rows={smUp ? 1 : 2}
                  multiline
                  placeholder="우리 회사와의 커피챗에 관심을 가질 수 있도록 타이틀을 입력해 주세요."
                  helperText="예) 경력직 채용 시장에 관심이 있다면, DIO 팀을 만나보세요!"
                  FormHelperTextProps={{ sx: { ml: 0 } }}
                  value={appealTitle}
                  onChange={(e) => setAppealTitle(e.target.value)}
                />
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                만나보고 싶은 포지션
              </Typography>
              <Box
                sx={{
                  px: 2,
                  py: selection?.length > 0 ? 1.5 : 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1,
                  "&:hover": {
                    borderColor: "primary.main",
                    cursor: "pointer",
                  },
                }}
                onClick={() => setOpenPositionDialog(true)}
              >
                {(selection && selection.length > 0) ? (
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {selection.map((s, index) => (
                      <Chip key={index} label={`${s.role}${s.position === 'developer' ? ' 개발자' : ''}`} />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ color: "text.disabled" }}>
                    클릭하여 선택하기
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                만나보고 싶은 이유
              </Typography>
              <Box
                sx={{
                  px: 2,
                  py: (needCasualChat || needPartTime || needMiniProject) ? 1.5 : 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1,
                  "&:hover": {
                    borderColor: "primary.main",
                    cursor: "pointer",
                  },
                }}
                onClick={() => setOpenReasonDialog(true)}
              >
                {(needCasualChat || needPartTime || needMiniProject) ? (
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {needCasualChat && <Chip label="캐주얼 커피챗" />}
                    {needPartTime && <Chip label="파트타임" />}
                    {needMiniProject && <Chip label="미니 프로젝트" />}
                  </Stack>
                ) : (
                  <Typography sx={{ color: "text.disabled" }}>
                    클릭하여 선택하기
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                우리 회사 키워드 (선택)
              </Typography>
              <Autocomplete
                multiple
                id="profile-keywords"
                options={['키워드를 공백없이 입력 후 엔터키를 눌러주세요']}
                freeSolo
                clearOnBlur
                value={companyKeywords}
                onChange={(e, newValue) => 
                  {
                    newValue = newValue?.map(v => `${v.replace(/\s/g, '').replace(/^#*/, '#')}`)
                    setCompanyKeywords(newValue);
                  }
                }
                getOptionDisabled={(option) => option === '키워드를 공백없이 입력 후 엔터키를 눌러주세요'}
                renderTags={(value, getTagProps) => value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="회사와 연관된 키워드를 추가해주세요."
                    helperText="예) YCombinator, 초기멤버, 소수정예, 네이버출신 등"
                    FormHelperTextProps={{ sx: { ml: 0 } }}
                  />
                )}
                filterOptions={(options) => {
                  return options;
                }}
              />
            </Stack>
          </Stack>
          {/* _card form */}

        </CardContent>
      </Card>

      {/* preview */}
      <Box>
        <Stack direction="row" alignItems="flex-start" spacing={.5} flexWrap sx={{ mb: 1 }}>
          <Iconify icon="eva:eye-fill" color="info.main" sx={{ width: 24, height: 24 }} />
          <Typography variant="subtitle1" color="info.main">
            회사 소개는 크루에게 이렇게 보여져요.
          </Typography>
        </Stack>
        <Card
          sx={{ backgroundColor: "grey.100" }}
        >
          <CardContent>
            <Box
              sx={{
                backgroundColor: "common.white",
                borderRadius: 3,
                p: 3,
                width: 1,
              }}
            >
              <Stack direction="row" spacing={10}>
                <Stack direction={{ xs: "column", sm: "row" }} sx={{ width: 1, overflow: "clip" }} spacing={3}>
                  <Stack direction="row" justifyContent={"space-between"} alignItems={"flex-start"}>
                    <Box
                      sx={{
                        borderRadius: 2,
                        minWidth: 72,
                        width: 72, height: 72
                      }}
                    >
                      <Image
                        src={fileBlob ? URL.createObjectURL(fileBlob) : profile.logo_file_path || 'https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/fliers-no-logo-2x.png'}
                        alt="company-logo"
                        sx={{ width: 72, height: 72, minWidth: 72, minHeight: 72, borderRadius: 2 }}
                      />
                    </Box>
                  </Stack>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ color: profileName ? "info.dark" : "text.secondary" }}>
                        {profileName || '회사 또는 서비스 이름'}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle1" sx={{ color: appealTitle ? "text.primary" : "text.disabled" }}>
                      {appealTitle || '커피챗 어필 타이틀'}
                    </Typography>
                    <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1 }}>
                      {companyKeywords?.map((keyword, index) => (
                        <Chip key={index} label={keyword} color="info" size="small" variant="outlined" sx={{ borderColor: "info.main" }} />
                      ))}
                    </Stack>
                    <Stack sx={{ pt: 2 }} spacing={1}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.disabled">
                          만나보고 싶은 포지션
                        </Typography>
                        <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1, minHeight: '32px' }}>
                          {selection.map((s, index) => (
                            <Chip key={index} label={`${s.role}${s.position === 'developer' ? ' 개발자' : ''}`} />
                          ))}
                        </Stack>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.disabled">
                          만나보고 싶은 이유
                        </Typography>
                        <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1, minHeight: '32px' }}>
                          {needCasualChat && <Chip label="캐주얼 커피챗" />}
                          {needPartTime && <Chip label="파트타임" />}
                          {needMiniProject && <Chip label="미니 프로젝트" />}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
                {smUp && (
                  <Stack
                    sx={{ minWidth: '150px' }}
                    alignItems="flex-end"
                  >
                    <Button
                      disableRipple
                      variant="text"
                      size="small"
                      sx={{ color: "info.dark" }}
                      endIcon={<Iconify icon="ic:chevron-right" />}
                    >
                      회사 정보 자세히보기
                    </Button>
                  </Stack>
                )}
              </Stack>
              {!smUp && (
                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="text"
                    sx={{ color: "info.dark" }}
                    endIcon={<Iconify icon="ic:chevron-right" />}
                    disableRipple
                    size="small"
                  >
                    회사 정보 자세히보기
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* detail */}
      <Card sx={{ overflow: "visible" }}>
        <CardContent>
          <Typography variant="h6">
            회사 상세 정보
          </Typography>

          {/* detail form */}
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                자세한 회사 소개 작성
              </Typography>
              <Typography variant="caption" color="text.secondary">
                작성하신 회사 소개 내용이 그대로 크루에게 보여집니다. 크루가 우리 회사에 관심을 가지도록 작성해주세요.
              </Typography>
              <TextField
                multiline
                minRows={smUp ? 6 : 4}
                placeholder="크루에게 어필할 수 있는 회사 소개를 작성해주세요."
                value={quillSimple}
                onChange={(e) => setQuillSimple(e.target.value)}
              />
              {/* <Editor
                simple
                id="simple-editor"
                value={quillSimple}
                onChange={(value) => setQuillSimple(value)}
                placeholder="크루에게 어필할 수 있는 회사 소개를 작성해주세요."
                sx={{
                  overflow: "visible",
                }}
              /> */}
            </Stack>
          </Stack>
          {/* _detail form */}

          <Button
            startIcon={<Iconify icon="eva:info-fill" />}
            sx={{ color: "text.secondary", mx: -1, mt: 1 }}
            onClick={() => setOpenFliersProfileExampleDialog(true)}
          >
            다른 회사의 소개글 예시 확인하기
          </Button>

        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={3} justifyContent="flex-end">
        {profile?.status === 'draft' && (
        <LoadingButton
          variant="outlined"
          color="inherit"
          size="large"
          sx={{ px: 6 }}
          loading={isSubmitting}
          onClick={saveProfile}
        >
          임시저장
        </LoadingButton>
        )}
        <LoadingButton
          disabled={!quillSimple || !profileName || !appealTitle || selection.length === 0 || (!needCasualChat && !needPartTime && !needMiniProject)}
          variant="contained"
          size="large"
          sx={{ px: 6 }}
          loading={isSubmitting}
          onClick={submitProfile}
        >
          {profile?.status === 'draft' ? '제출하기' : '수정하기'}
        </LoadingButton>
      </Stack>
    </Stack>
    <FliersProfilePositionDialog
      isOpen={openPositionDialog}
      closeDialog={() => setOpenPositionDialog(false)}
      selectedPosition={selection}
      updatePosition={updatePosition}
    />
    <FliersProfileReasonDialog
      isOpen={openReasonDialog}
      closeDialog={() => setOpenReasonDialog(false)}
      origCasualChat={needCasualChat}
      setOrigCasualChat={setNeedCasualChat}
      origPartTime={needPartTime}
      setOrigPartTime={setNeedPartTime}
      origMiniProject={needMiniProject}
      setOrigMiniProject={setNeedMiniProject}
    />
    <FliersProfileExampleDialog
      isOpen={openFliersProfileExampleDialog}
      closeDialog={() => setOpenFliersProfileExampleDialog(false)}
    />
    </>
  )
}